import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { X, Settings2, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { logInfo, logWarning } from "@/lib/logger";

// Use versioned key to force re-consent when structure changes
const CONSENT_KEY = "gdpr-consent-v2";

// COOKIE-001: In-memory fallback so banner stays hidden this session if localStorage is blocked/cleared (e.g. Brave)
let gdprConsentAcceptedThisSession = false;

interface ConsentSettings {
  necessary: boolean;
  analytics: boolean;
  version: number;
  timestamp: string;
}

const CONSENT_VERSION = 2;

const defaultConsent: ConsentSettings = {
  necessary: true,
  analytics: false,
  version: CONSENT_VERSION,
  timestamp: "",
};

// Helper to check if consent is valid
function isValidConsent(data: unknown): data is ConsentSettings {
  if (!data || typeof data !== "object") return false;
  const obj = data as Record<string, unknown>;

  // Legacy compatibility: earlier versions might miss timestamp.
  const hasTimestamp = typeof obj.timestamp === "string" && obj.timestamp.length > 0;

  return (
    typeof obj.necessary === "boolean" &&
    typeof obj.analytics === "boolean" &&
    (hasTimestamp || obj.timestamp === undefined || obj.timestamp === "") &&
    (obj.version === CONSENT_VERSION || obj.version === undefined)
  );
}

function normalizeConsent(input: Partial<ConsentSettings>): ConsentSettings {
  return {
    necessary: input.necessary ?? true,
    analytics: input.analytics ?? false,
    version: CONSENT_VERSION,
    timestamp: input.timestamp && input.timestamp.length > 0 ? input.timestamp : new Date().toISOString(),
  };
}

export function GDPRConsentManager() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [consent, setConsent] = useState<ConsentSettings>(defaultConsent);

  // Check for existing consent on mount
  useEffect(() => {
    if (gdprConsentAcceptedThisSession) {
      return;
    }
    try {
      // 1) Current key
      const stored = localStorage.getItem(CONSENT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (isValidConsent(parsed)) {
          const normalized = normalizeConsent(parsed as Partial<ConsentSettings>);
          // Persist timestamp so banner does not reappear within the same session.
          // Also verify the timestamp was actually saved by reading it back
          localStorage.setItem(CONSENT_KEY, JSON.stringify(normalized));
          
          // Double-check it was saved correctly
          const verifyStored = localStorage.getItem(CONSENT_KEY);
          if (verifyStored) {
            const verifyParsed = JSON.parse(verifyStored);
            if (verifyParsed?.timestamp) {
              gdprConsentAcceptedThisSession = true;
              setConsent(normalized);
              logInfo("[GDPR] Consent found and verified, banner hidden");
              return;
            } else {
              logWarning("[GDPR] Consent timestamp not persisted correctly, re-saving...");
              // Force re-save with fresh timestamp
              const fixed = normalizeConsent({ necessary: normalized.necessary, analytics: normalized.analytics });
              localStorage.setItem(CONSENT_KEY, JSON.stringify(fixed));
              gdprConsentAcceptedThisSession = true;
              setConsent(fixed);
              return;
            }
          }
        } else {
          logWarning("[GDPR] Invalid consent format, clearing and showing banner");
          localStorage.removeItem(CONSENT_KEY);
        }
      }

      // 2) Legacy keys
      const legacyAccepted = localStorage.getItem("findigo-cookie-consent");
      if (legacyAccepted === "accepted") {
        const normalized = normalizeConsent({ necessary: true, analytics: false });
        localStorage.setItem(CONSENT_KEY, JSON.stringify(normalized));
        gdprConsentAcceptedThisSession = true;
        setConsent(normalized);
        return;
      }
    } catch {
      // localStorage unavailable or parse error - show banner
    }

    // No valid consent - show banner after delay
    const timer = setTimeout(() => setIsVisible(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Listen for custom event to open settings (from Footer)
  useEffect(() => {
    const handleOpenSettings = () => {
      setIsVisible(true);
      setShowSettings(true);
    };
    
    window.addEventListener("open-cookie-settings", handleOpenSettings);
    return () => window.removeEventListener("open-cookie-settings", handleOpenSettings);
  }, []);

  const saveConsent = async (settings: Omit<ConsentSettings, "timestamp" | "version">) => {
    gdprConsentAcceptedThisSession = true;
    setIsVisible(false);
    setShowSettings(false);
    const fullSettings: ConsentSettings = {
      ...settings,
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
    };
    try {
      localStorage.setItem(CONSENT_KEY, JSON.stringify(fullSettings));
      setConsent(fullSettings);
      
      // GDPR: Log consent for compliance
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          // Log analytics consent if given
          if (settings.analytics) {
            await supabase.from("consent_logs").insert({
              user_id: session.user.id,
              consent_type: "analytics",
              user_agent: navigator.userAgent,
            });
          }
          
          // Log necessary cookies consent (always true, but log for compliance)
          await supabase.from("consent_logs").insert({
            user_id: session.user.id,
            consent_type: "necessary",
            user_agent: navigator.userAgent,
          });
        }
      } catch (error) {
        logWarning("[GDPR] Failed to log consent:", error);
      }
    } catch {
      // localStorage unavailable - banner already hidden via gdprConsentAcceptedThisSession
    }
    window.dispatchEvent(new Event("analytics-consent-updated"));
  };

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true });
  };

  const handleAcceptNecessary = () => {
    saveConsent({ necessary: true, analytics: false });
  };

  const handleSaveSettings = () => {
    saveConsent({ necessary: consent.necessary, analytics: consent.analytics });
  };

  if (!isVisible) return null;

  return (
    <div
      role="region"
      aria-label="Cookie- och integritetsval"
      className="fixed bottom-0 left-0 right-0 z-[45] p-4 bg-background border-t border-border shadow-lg animate-in slide-in-from-bottom-5 duration-300"
    >
      <div className="max-w-4xl mx-auto">
        {!showSettings ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Shield className="h-5 w-5 text-primary" aria-hidden />
              </div>
              <div>
                <p className="text-sm text-foreground font-medium mb-1">
                  Din integritet är viktig för oss
                </p>
                <p className="text-xs text-muted-foreground">
                  Vi använder cookies för att hålla dig inloggad och förbättra tjänsten. 
                  Inga tredjepartscookies för reklam.{" "}
                  <a href="/privacy" className="inline-flex items-center min-h-[var(--touch-target)] py-2 -my-1 text-primary hover:underline rounded-lg">
                    Läs mer
                  </a>
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="text-xs min-h-[var(--touch-target)]"
                aria-label="Öppna cookie-inställningar"
              >
                <Settings2 className="h-4 w-4 mr-1" aria-hidden />
                Inställningar
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAcceptNecessary}
                className="text-xs min-h-[var(--touch-target)]"
                aria-label="Acceptera endast nödvändiga cookies"
              >
                Endast nödvändiga
              </Button>
              <Button type="button" size="sm" onClick={handleAcceptAll} className="text-xs min-h-[var(--touch-target)]" aria-label="Acceptera alla cookies">
                Acceptera alla
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-foreground">Cookie-inställningar</h3>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowSettings(false)}
                className="min-h-[var(--touch-target)] min-w-[var(--touch-target)]"
                aria-label="Stäng inställningar"
              >
                <X className="h-4 w-4" aria-hidden />
              </Button>
            </div>

            <div className="space-y-4">
              {/* Necessary cookies */}
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50">
                <div className="flex-1">
                  <Label className="text-sm font-medium">Nödvändiga</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Krävs för att hålla dig inloggad och spara dina preferenser. Kan inte stängas av.
                  </p>
                </div>
                <Switch checked disabled className="opacity-50" />
              </div>

              {/* Analytics cookies */}
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg bg-muted/50">
                <div className="flex-1">
                  <Label className="text-sm font-medium">Analys</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Hjälper oss förstå hur tjänsten används så vi kan förbättra den. Ingen personlig spårning.
                  </p>
                </div>
                <Switch
                  checked={consent.analytics}
                  onCheckedChange={(checked) =>
                    setConsent((prev) => ({ ...prev, analytics: checked }))
                  }
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
              <Button type="button" variant="outline" size="sm" onClick={handleAcceptNecessary} className="min-h-[var(--touch-target)]" aria-label="Spara endast nödvändiga">
                Endast nödvändiga
              </Button>
              <Button type="button" size="sm" onClick={handleSaveSettings} className="min-h-[var(--touch-target)]" aria-label="Spara val">
                Spara val
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Re-export hook from dedicated file for backwards compatibility
export { useAnalyticsConsent } from "@/hooks/useAnalyticsConsent";
